import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { JAPANESE_LOCALE, LOCALE_OPTIONS } from '@link/share/constants';
import { useSupportedLanguageCodes } from '@link/share/hooks';
import { LanguageCodeType } from '@link/share/services/types';
import { buildLocaleList, updateThemeColor } from '@link/share/utils';
import Logo from '@link/skytree/assets/skytree.png';
import { SHUTTO_LOCALE_OPTIONS } from '@link/skytree/i18n';
import BaseLayout from '@link/skytree/layouts/Base';
import { useStore } from '@link/skytree/stores';
import skytreeAuth from '@link/skytree/vendors/firebase';

const SkytreeLayout = observer(() => {
  const { i18n } = useTranslation();
  const { auth, site, account, product } = useStore();
  useEffect(() => {
    site.getSiteSchema(i18n.language.toUpperCase() as LanguageCodeType);
  }, [i18n.language, site]);

  useEffect(() => {
    updateThemeColor(site.siteSchema?.siteLayout?.primaryColor);
  }, [site.siteSchema?.siteLayout?.primaryColor]);

  const logout = useCallback(async () => {
    await skytreeAuth.logout();
    account.setAccountInfo({});
  }, [account]);

  const { isProduct, supportedLanguageCodes } = useSupportedLanguageCodes(
    site.supportedLanguageCodes,
    product.product.supportedLanguageCodes
  );

  const isNotJapanese = useMemo(
    () => i18n.language !== JAPANESE_LOCALE,
    [i18n.language]
  );

  const logoUrl = useMemo(() => {
    return site.logoUrl ?? Logo;
  }, [site.logoUrl]);

  const localeList = useMemo(() => {
    const baseLocales = buildLocaleList(LOCALE_OPTIONS, supportedLanguageCodes);

    if (isProduct) {
      return isNotJapanese
        ? [...baseLocales, ...SHUTTO_LOCALE_OPTIONS]
        : baseLocales;
    }
    return [...baseLocales, ...SHUTTO_LOCALE_OPTIONS];
  }, [isNotJapanese, isProduct, supportedLanguageCodes]);

  return (
    <BaseLayout
      appLogo={logoUrl}
      logout={logout}
      loggedIn={auth.loggedIn}
      isPasswordAuth={auth.isPasswordAuth}
      appTitle="Skytree"
      showCurrency={site.showOtherCurrencies}
      supportedCurrencies={site.supportedCurrencies}
      currentCurrency={site.currency}
      onCurrencyChange={currency => site.setCurrency(currency)}
      localeList={localeList}
    />
  );
});

export { SkytreeLayout };
